import { useRequest } from 'features/request/useRequest';
import type { ContractOtherStatusEnum, OtherCostCalculationTypeEnum } from 'gql/graphql';
import { ProjectMethodologyEnum, SortEnumType } from 'gql/graphql';
import { pushIfValue } from 'utils';

import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { bimContractOtherCostsGql, uerContractOtherCostsGql } from '../contractOthersQueries';

interface IUseContractOtherCosts {
    contractId: number;
    currentPage?: number;
    currentPageSize?: number;
    constructionTypeId: number;
    refreshFilters?: () => void;
    methodology: ProjectMethodologyEnum;
}

const getWhere = (filterValues?: Record<string, string[]>) => {
    if (!filterValues) {
        return undefined;
    }

    return {
        and: Object.entries(filterValues).reduce((acc: any, [key, value]) => {
            if (!value.length) {
                return acc;
            }

            if (
                key === 'isIncludedInWorkOrder' ||
                key === 'isIncludedInActKc2' ||
                key === 'isIncludedInWorkOrderChange'
            ) {
                return [
                    ...acc,
                    {
                        [key]: { eq: value[0] === 'Да' },
                    },
                ];
            }

            if (key === 'constructionTypeLimits?.[0].relationStatus') {
                return [
                    ...acc,
                    {
                        constructionTypeLimits: {
                            all: {
                                relationStatus: {
                                    in: value,
                                },
                            },
                        },
                    },
                ];
            }

            return [
                ...acc,
                {
                    [key]: { in: value },
                },
            ];
        }, []),
    };
};

export const useContractOtherCosts = (props: IUseContractOtherCosts) => {
    const dispatch = useDispatch();
    const { contractId, currentPageSize, currentPage, constructionTypeId, refreshFilters, methodology } = props;

    const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});
    const [namesFilter, setNamesFilter] = useState<string[]>([]);
    const [calculatingTypesFilter, setCalculatingTypesFilter] = useState<OtherCostCalculationTypeEnum[]>([]);
    const [statusesFilter, setStatusesFilter] = useState<ContractOtherStatusEnum[]>([]);
    const [isIncludedInActKc2Filter, setIsIncludedInActKc2Filter] = useState<number | undefined>(undefined);
    const [isIncludedInWorkOrderFilter, setIsIncludedInWorkOrderFilter] = useState<number | undefined>(undefined);

    const changeFilterHandler = useCallback(({ fieldName, checkedValues }) => {
        setFilterValues((prevValue) => ({
            ...prevValue,
            [fieldName]: checkedValues,
        }));
    }, []);

    const onFilterChange = useCallback((value) => {
        if (value?.name?.value?.length) {
            setNamesFilter(value.name.value.map((v: { data: { value: any } }) => v.data.value));
        } else {
            setNamesFilter([]);
        }

        if (value?.calculatingType?.value?.length) {
            setCalculatingTypesFilter(value?.calculatingType?.value.map((v: { value: any }) => v.value));
        } else {
            setCalculatingTypesFilter([]);
        }

        if (value?.status?.value?.length) {
            setStatusesFilter(value?.status?.value.map((v: { value: any }) => v.value));
        } else {
            setStatusesFilter([]);
        }

        setIsIncludedInActKc2Filter(value?.isIncludedInActKc2?.value?.value);
        setIsIncludedInWorkOrderFilter(value?.isIncludedInWorkOrder?.value?.value);
    }, []);

    const isUer = useMemo(() => methodology === ProjectMethodologyEnum.Uer, [methodology]);

    const variables = useMemo(() => {
        const name = namesFilter?.length ? { name: { in: namesFilter } } : undefined;
        const calculatingType = calculatingTypesFilter?.length
            ? { calculatingType: { in: calculatingTypesFilter } }
            : undefined;
        const status = statusesFilter?.length ? { status: { in: statusesFilter } } : undefined;
        const isIncludedInActKc2 = Number.isFinite(isIncludedInActKc2Filter)
            ? { isIncludedInActKc2: { eq: isIncludedInActKc2Filter === 1 } }
            : undefined;
        const isIncludedInWorkOrder = Number.isFinite(isIncludedInWorkOrderFilter)
            ? { isIncludedInWorkOrder: { eq: isIncludedInWorkOrderFilter === 1 } }
            : undefined;

        const and: any[] = [];

        pushIfValue(and, name);
        pushIfValue(and, calculatingType);
        pushIfValue(and, status);
        pushIfValue(and, isIncludedInActKc2);
        pushIfValue(and, isIncludedInWorkOrder);

        const where = and?.length
            ? {
                  and,
              }
            : undefined;

        const order = [{ name: SortEnumType.Asc }];

        return {
            contractId,
            constructionTypeId,
            skip: currentPageSize ? (currentPage || 0) * currentPageSize : undefined,
            take: currentPageSize,
            where,
            order,
        };
    }, [
        calculatingTypesFilter,
        constructionTypeId,
        contractId,
        currentPage,
        currentPageSize,
        isIncludedInActKc2Filter,
        isIncludedInWorkOrderFilter,
        namesFilter,
        statusesFilter,
    ]);

    const variablesUer = useMemo(
        () => ({
            contractId,
            constructionTypeId,
            skip: currentPageSize ? (currentPage || 0) * currentPageSize : undefined,
            take: currentPageSize,
            where: getWhere(filterValues),
            order: [{ name: SortEnumType.Asc }],
        }),
        [constructionTypeId, contractId, currentPage, currentPageSize, filterValues],
    );

    const [
        contractOtherCostsData,
        contractOtherCostsStatus,
        contractOtherCostsError,
        contractOtherCostsMakeRequest,
        contractOtherCostsSetData,
    ] = useRequest({
        requestId: 'contractOtherCosts',
        query: uerContractOtherCostsGql,
        withEffect: isUer && Boolean(contractId) && Boolean(constructionTypeId),
        variables: variablesUer,
    });

    const [
        bimContractOtherCostsData,
        bimContractOtherCostsStatus,
        bimContractOtherCostsError,
        bimContractOtherCostsMakeRequest,
        bimContractOtherCostsSetData,
    ] = useRequest({
        requestId: 'contractOtherCosts',
        query: bimContractOtherCostsGql,
        withEffect: !isUer && Boolean(contractId) && Boolean(constructionTypeId),
        variables,
    });

    const doReload = useCallback(() => {
        if (isUer) {
            dispatch(contractOtherCostsMakeRequest(variablesUer));
        } else {
            dispatch(bimContractOtherCostsMakeRequest(variables));
        }

        refreshFilters?.();
    }, [
        bimContractOtherCostsMakeRequest,
        contractOtherCostsMakeRequest,
        dispatch,
        isUer,
        refreshFilters,
        variables,
        variablesUer,
    ]);

    const data = useMemo(
        () =>
            isUer ? contractOtherCostsData?.uerContractOtherCosts : bimContractOtherCostsData?.bimContractOtherCosts,
        [bimContractOtherCostsData?.bimContractOtherCosts, contractOtherCostsData?.uerContractOtherCosts, isUer],
    );

    const status = useMemo(
        () => (isUer ? contractOtherCostsStatus : bimContractOtherCostsStatus),
        [bimContractOtherCostsStatus, contractOtherCostsStatus, isUer],
    );

    const error = useMemo(
        () => (isUer ? contractOtherCostsError : bimContractOtherCostsError),
        [bimContractOtherCostsError, contractOtherCostsError, isUer],
    );

    const makeRequest = useMemo(
        () => (isUer ? contractOtherCostsMakeRequest : bimContractOtherCostsMakeRequest),
        [bimContractOtherCostsMakeRequest, contractOtherCostsMakeRequest, isUer],
    );

    const setData = useMemo(
        () => (isUer ? contractOtherCostsSetData : bimContractOtherCostsSetData),
        [bimContractOtherCostsSetData, contractOtherCostsSetData, isUer],
    );

    return [data, status, error, makeRequest, setData, onFilterChange, doReload, changeFilterHandler];
};
